// SimpleLineChart.tsx
import { memo, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Label,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import styled from 'styled-components';
import { axisMaxByMSD, toNum } from '../chartUtils';
import { NPLTrendRow } from './NPLTrendAnalysisGraph';

type Props = {
  data: any[];
  dataKey1: keyof NPLTrendRow;
  dataKey2: keyof NPLTrendRow;
  color1: string;
  color2: string;
  label1: string;
  label2: string;
  yLabel?: string; // 좌측 축 제목
  unit?: '%' | '명' | '';
  height?: number;
  leftAxisWidth?: number;
};

const H_DEFAULT = 320;
const TICK_W_DEFAULT = 56;
const LEFT_W_DEFAULT = 50;

// Tooltip indicator 이미지 맵핑
const legendImageMap: Record<string, string> = {
  '낙찰률': '/assets/images/icon/green_dot.svg',
  '낙찰가율': '/assets/images/icon/blue_dot.svg',
  '담보가치율': '/assets/images/icon/red_dot.svg',
  '회수율': '/assets/images/icon/purple_dot.svg',
  '손실률': '/assets/images/icon/orange_dot.svg',
  '회수부족율': '/assets/images/icon/gray_dot.svg'
};

function CustomTooltip({
  active,
  payload,
  label,
  label1,
  label2,
  color1,
  color2,
  unit,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
  label1: string;
  label2: string;
  color1: string;
  color2: string;
  unit?: string;
}) {
  if (!active || !payload || !payload.length) return null;

  // key -> label, color 매핑
  const metaConfig = [
    {
      label: label1,
      color: color1,
      img: legendImageMap[label1] || '/assets/images/icon/green_dot.svg',
    },
    {
      label: label2,
      color: color2,
      img: legendImageMap[label2] || '/assets/images/icon/blue_dot.svg',
    }
  ];

  return (
    <TooltipContainer>
      <TooltipHeader>{label}</TooltipHeader>
      <SeriesList>
        {metaConfig.map((meta, idx) => {
          // payload 항목을 라벨 기준으로 찾음
          const pv = payload.find((p) => p.name === meta.label);
          if (!pv || pv.value === undefined || pv.value === null || Number.isNaN(pv.value)) return null;
          let displayValue;
          if (unit === '%') {
            displayValue = (<>{Number(pv.value).toFixed(1)}%</>);
          } else if (unit === '명') {
            displayValue = (<>{Number(pv.value).toFixed(1)}명</>);
          } else {
            displayValue = (<>{Number(pv.value).toLocaleString()}</>);
          }
          return (
            <SeriesItem key={meta.label}>
              <SeriesIcon>
                <img src={meta.img} alt="" width={16} height={16} />
              </SeriesIcon>
              <SeriesLabel>{meta.label}</SeriesLabel>
              <SeriesValue style={{ marginLeft: 'auto' }}>{displayValue}</SeriesValue>
            </SeriesItem>
          );
        })}
      </SeriesList>
    </TooltipContainer>
  );
}

function SimpleLineChartBase({
  data,
  dataKey1,
  dataKey2,
  color1,
  color2,
  label1,
  label2,
  yLabel = '비율',
  unit = '%',
  height = H_DEFAULT,
  leftAxisWidth = LEFT_W_DEFAULT,
}: Props) {
  const rateAxisMaxFor = (keys: (keyof NPLTrendRow)[]) =>
    axisMaxByMSD(
      Math.max(
        0,
        ...data.map((d) => Math.max(...keys.map((k) => toNum(d[k])))),
      ),
    );

  const yMax = useMemo(
    () => rateAxisMaxFor([dataKey1, dataKey2]),
    [data, dataKey1, dataKey2],
  );

  const innerWidth = Math.max(TICK_W_DEFAULT * data.length, 480);

  return (
    <Wrap data-capture-section>
      <Grid style={{ gridTemplateColumns: `${leftAxisWidth}px minmax(0,1fr)` }}>
        {/* 좌측 축만 */}
        <AxisPane>
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
              data={data}
              margin={{ top: 22, right: 10, left: -10, bottom: 38 }}
            >
              <XAxis dataKey="label" hide />
              <YAxis
                domain={[0, yMax]}
                tick={{ fill: '#787D87', fontSize: 12 }}
                axisLine
                tickLine={{ stroke: '#787D87' }}
                tickFormatter={(v) => Number(v).toLocaleString()}
              >
                <Label
                  value={yLabel}
                  position="insideTopLeft"
                  offset={34}
                  dx={-10}
                  dy={-52}
                  style={{
                    fill: '#787D87',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                />
              </YAxis>
            </ComposedChart>
          </ResponsiveContainer>
        </AxisPane>

        {/* 가운데 플롯 + X축 (가로 스크롤) */}
        <ScrollPane>
          <Inner style={{ width: innerWidth, height }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 16, right: 24, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="label"
                  axisLine
                  tickLine={{ stroke: '#787D87' }}
                  tick={{ fill: '#787D87', fontSize: 12 }}
                  padding={{ left: 12, right: 12 }}
                />
                <YAxis hide />
                <Tooltip
                  content={({
                    active,
                    payload,
                    label: xLabel
                  }) => (
                    <CustomTooltip
                      active={active}
                      payload={payload}
                      label={xLabel}
                      label1={label1}
                      label2={label2}
                      color1={color1}
                      color2={color2}
                      unit={unit}
                    />
                  )}
                />
                <Line
                  type="linear"
                  dataKey={dataKey1}
                  name={label1}
                  stroke={color1}
                  strokeWidth={2}
                  dot={{ r: 3, strokeWidth: 0, fill: color1 }}
                  activeDot={{ r: 4 }}
                />
                <Line
                  type="linear"
                  dataKey={dataKey2}
                  name={label2}
                  stroke={color2}
                  strokeWidth={2}
                  dot={{ r: 3, strokeWidth: 0, fill: color2 }}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Inner>
        </ScrollPane>
      </Grid>
    </Wrap>
  );
}

export default memo(SimpleLineChartBase);

const TooltipContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0px 6px 30px 0px rgba(56, 65, 87, 0.18);
  padding: 16px 18px 12px 18px;
  min-width: 186px;
  max-width: 400px;
`;

const TooltipHeader = styled.div`
  font-weight: 500;
  font-size: 16px;
  leading-trim: NONE;
  line-height: 24px;
  letter-spacing: -2%;
  color: #424854;
  margin-bottom: 16px;
`;

const SeriesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SeriesItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SeriesIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 11px;
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
`;

const SeriesLabel = styled.span`
  font-size: 13px;
  color: #676e7e;
  min-width: 74px;
  margin-right: 12px;
  flex-shrink: 0;
  font-weight: 500;
`;

const SeriesValue = styled.span`
  font-size: 14px;
  color: #111322;
  font-weight: 500;
  white-space: nowrap;
`;


const Wrap = styled.div``;
const Grid = styled.div`
  display: grid;
  align-items: start;
  background: #fff;
`;
const AxisPane = styled.div`
  pointer-events: none;
`;
const ScrollPane = styled.div.attrs<{ 'data-export-expand'?: string }>(() => ({
  'data-export-expand': '',
}))`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;
const Inner = styled.div.attrs<{ 'data-export-inner'?: string }>(() => ({
  'data-export-inner': '',
}))`
  padding-bottom: 8px;
`;
